import React from 'react';
import { Trophy, AlertTriangle } from 'lucide-react';
import type { EntityRow } from './types';

interface RankingPanelProps {
  items: EntityRow[];
  metric: 'passPercentage' | 'performanceIndex';
  type: 'top' | 'bottom';
  title: string;
  onItemClick?: (name: string) => void;
}

const RankingPanel: React.FC<RankingPanelProps> = function ({ items, metric, type, title, onItemClick }) {
  const barColor = type === 'top' ? '#22c55e' : '#ef4444';
  const allVals = items.map(function (r) { return r[metric]; });
  const minVal = metric === 'passPercentage' ? Math.min(70, allVals.length ? Math.min.apply(null, allVals) : 70) : 0;
  const maxVal = allVals.length ? Math.max.apply(null, allVals) : 100;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        {type === 'top'
          ? <Trophy className="w-4 h-4 text-green-500" />
          : <AlertTriangle className="w-4 h-4 text-red-500" />}
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="space-y-3">
        {items.map(function (r, i) {
          return (
            <div
              key={r.name}
              className={
                'flex items-center gap-3 ' +
                (onItemClick ? 'cursor-pointer hover:bg-gray-50 rounded-lg px-1 -mx-1 transition-colors' : '')
              }
              onClick={function () { if (onItemClick) onItemClick(r.name); }}
            >
              <span
                className={
                  'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ' +
                  (type === 'top'
                    ? i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-green-400'
                    : 'bg-red-400')
                }
              >
                {type === 'top' ? i + 1 : '!'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-800 truncate pr-2" title={r.name}>{r.name}</span>
                  <span className="text-sm font-bold flex-shrink-0" style={{ color: barColor }}>{r[metric]}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full"
                    style={{
                      width: (maxVal > minVal ? ((r[metric] - minVal) / (maxVal - minVal)) * 100 : 100) + '%',
                      backgroundColor: barColor,
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">No data</p>
        )}
      </div>
    </div>
  );
};

export default RankingPanel;
